import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

export default function UserProfile() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    address: '',
    profileImage: '',
    createdAt: null
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/auth');
        return;
      }
      setUser(currentUser);
      try {
        const docRef = doc(db, "users", currentUser.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserData({
            name: data.name || '',
            phone: data.phone || '',
            email: currentUser.email,
            city: data.city || '',
            address: data.address || '',
            profileImage: data.profileImage || '',
            createdAt: data.createdAt || null
          });
        } else {
          setUserData({
            name: '',
            phone: '',
            email: currentUser.email,
            city: '',
            address: '',
            profileImage: '',
            createdAt: null
          });
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Profile Image Compression & Handler
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setMessage({ text: 'Image size must be less than 2MB.', type: 'error' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        const MAX_SIZE = 400;
        if (width > height) {
          if (width > MAX_SIZE) {
            height = Math.round((height * MAX_SIZE) / width);
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width = Math.round((width * MAX_SIZE) / height);
            height = MAX_SIZE;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        const webpBase64 = canvas.toDataURL('image/webp', 0.8);
        setUserData(prev => ({ ...prev, profileImage: webpBase64 }));
      };
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (!user) return;
    setUpdating(true);
    setMessage({ text: '', type: '' });

    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        name: userData.name,
        phone: userData.phone,
        city: userData.city,
        address: userData.address,
        profileImage: userData.profileImage,
        updatedAt: new Date()
      });
      setMessage({ text: 'Profile updated successfully!', type: 'success' });
    } catch (err) {
      console.error("Error updating profile:", err);
      setMessage({ text: 'Failed to update profile. Please try again.', type: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">
        <p className="text-slate-400 text-sm animate-pulse">Loading Profile...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6 md:p-12 font-sans">
      <div className="max-w-3xl mx-auto">
        
        {/* Header with Dynamic Profile Image */}
        <div className="bg-slate-900 border border-slate-800 p-8 rounded-3xl shadow-xl mb-8 flex flex-col sm:flex-row items-center gap-6">
          {userData.profileImage ? (
            <img 
              src={userData.profileImage} 
              alt="Profile" 
              className="w-20 h-20 rounded-2xl object-cover border-2 border-indigo-500 shadow-lg"
            />
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-indigo-600 flex items-center justify-center text-3xl font-extrabold text-white shadow-lg uppercase">
              {userData.name ? userData.name.charAt(0) : userData.email.charAt(0)}
            </div>
          )}

          <div className="text-center sm:text-left">
            <h1 className="text-2xl font-extrabold text-white">{userData.name || 'Valued Customer'}</h1>
            <p className="text-slate-400 text-sm mt-1">{userData.email}</p>
            {userData.city && <p className="text-slate-500 text-xs mt-0.5">📍 {userData.city}</p>}
            <span className="inline-block mt-3 bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 text-xs px-3 py-1 rounded-full font-semibold">
              Verified Real Estate Member
            </span>
          </div>
        </div>

        {/* Status Message */}
        {message.text && (
          <div className={`p-4 mb-6 rounded-2xl text-sm border font-medium ${
            message.type === 'success' ? 'bg-emerald-950/40 border-emerald-500/40 text-emerald-400' : 'bg-rose-950/40 border-rose-500/40 text-rose-400'
          }`}>
            {message.text}
          </div>
        )}

        {/* Profile Update Form */}
        <div className="bg-slate-900 border border-slate-800 p-8 rounded-3xl shadow-xl">
          <h2 className="text-xl font-bold mb-6 text-white">Edit Profile Details</h2>
          
          <form onSubmit={handleUpdateProfile} className="space-y-6">
            
            {/* Profile Photo Upload Field */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Profile Picture</label>
              <input 
                type="file" 
                accept="image/png, image/jpeg, image/jpg, image/webp"
                onChange={handleImageChange}
                className="w-full bg-slate-800 border border-slate-700 text-slate-300 px-4 py-3 rounded-xl text-xs focus:outline-none file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-indigo-600 file:text-white hover:file:bg-indigo-700 cursor-pointer"
              />
              <p className="text-slate-500 text-xs mt-1">Upload JPEG, PNG or WEBP (Max 2MB).</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Full Name</label>
                <input 
                  type="text" 
                  value={userData.name}
                  onChange={(e) => setUserData({ ...userData, name: e.target.value })}
                  placeholder="Enter your full name"
                  required
                  className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Phone Number</label>
                <input 
                  type="tel" 
                  value={userData.phone}
                  onChange={(e) => setUserData({ ...userData, phone: e.target.value })}
                  placeholder="e.g. +91 9876543210"
                  required
                  className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-indigo-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">City / Location</label>
                <input 
                  type="text" 
                  value={userData.city}
                  onChange={(e) => setUserData({ ...userData, city: e.target.value })}
                  placeholder="e.g. Mumbai, Delhi"
                  className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Email Address (Read-only)</label>
                <input 
                  type="email" 
                  value={userData.email}
                  disabled
                  className="w-full bg-slate-800/50 border border-slate-700/50 text-slate-400 px-4 py-3 rounded-xl text-sm cursor-not-allowed"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-400 uppercase mb-2">Residential Address</label>
              <textarea 
                rows="3"
                value={userData.address}
                onChange={(e) => setUserData({ ...userData, address: e.target.value })}
                placeholder="Enter your complete address"
                className="w-full bg-slate-800 border border-slate-700 text-white px-4 py-3 rounded-xl text-sm focus:outline-none focus:border-indigo-500 resize-none"
              ></textarea>
            </div>

            <button 
              type="submit"
              disabled={updating}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3.5 rounded-xl transition shadow-lg text-sm disabled:opacity-50"
            >
              {updating ? 'Saving Changes...' : 'Save Profile Changes'}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
}